import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Card, Field, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { cn } from '@/lib/cn'
import { CARD_OPTIONS } from '@/features/onboarding/onboarding-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import select from '@/features/onboarding/SelectCard.module.css'

const PRICE: Record<string, number> = { virtual: 9, physical: 50, agent: 0, both: 55 }

/* An agent card is software's card, so what it costs is not the question —
   what it may do is. These are the guardrails set before it is issued. */
const CAPS = [25, 60, 150, 400]
const MERCHANTS = ['Soriana', 'Chedraui', 'Farmacias del Ahorro', 'Uber', 'Amazon', 'Rappi']

const CASHBACK = {
  icon: 'redeem',
  title: '0.5% cashback on everything',
  body: 'Paid in cash into your USD account on the 1st.',
}
const FX = {
  icon: 'public',
  title: 'Spend in any currency',
  body: '0.9% spread when it converts. Nothing when it does not.',
}

/* What is included genuinely differs by card, and the wallets are the part
   people assume wrongly: our issuer provisions them from the plastic only. */
function includedFor(choice: string) {
  if (choice === 'agent') {
    return [
      { icon: 'shield', title: 'Limits it cannot cross', body: 'Per transaction, per month, and only the merchants you pick.' },
      { icon: 'block', title: 'Revoke in one tap', body: 'It stops authorising immediately.' },
      { icon: 'history', title: 'Every attempt logged', body: 'Including the ones you declined it.' },
      { ...FX },
    ]
  }
  if (choice === 'virtual') {
    return [
      { icon: 'bolt', title: 'Ready in seconds', body: 'Number, expiry and CVV straight away, for online payments.' },
      { icon: 'sync', title: 'Regenerate the number any time', body: 'The old one stops working at once.' },
      CASHBACK,
      FX,
    ]
  }
  return [
    { icon: 'contactless', title: 'Apple Pay, Google Pay and Samsung Pay', body: 'All three, no extra charge per payment.' },
    CASHBACK,
    FX,
    { icon: 'lock', title: 'Freeze it from the app', body: 'Instant, reversible, and free.' },
  ]
}

const AFTER = [
  { label: 'Monthly fee', value: 'Free' },
  { label: 'Top up from your balance', value: 'Free' },
  { label: 'Top up from a debit card', value: '0.5%' },
  { label: 'Top up from a credit card', value: '2.5%' },
  { label: 'Spending in your own currency', value: 'Free' },
  { label: 'Spending that converts', value: '0.9%' },
  { label: 'ATMs', value: '2% after $200/mo' },
]

export function OpenCardScreen() {
  const navigate = useNavigate()
  const [choice, setChoice] = useState<string>('virtual')
  const [task, setTask] = useState('')
  const [cap, setCap] = useState(60)
  const [merchants, setMerchants] = useState<readonly string[]>(['Soriana'])
  const price = PRICE[choice] ?? 9
  const isAgent = choice === 'agent'

  return (
    <>
      <PageHeader
        title="Open a card"
        subtitle="Pay once. No monthly fee, no minimum spend, and 0.5% back on everything."
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Which card do you want?
            </Text>
            <div className={`${select.options} pb-mt-4`}>
              {CARD_OPTIONS.map((option) => {
                const isOn = choice === option.id
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={cn(select.option, isOn && select.selected, option.id === 'both' && select.wide)}
                    onClick={() => setChoice(option.id)}
                    aria-pressed={isOn}
                  >
                    <Icon name={option.icon} size={24} />
                    <Stack gap={1}>
                      <Stack direction="row" align="baseline" gap={2}>
                        <Text variant="caption" weight="semibold">
                          {option.title}
                        </Text>
                        <Text variant="eyebrow" weight="bold" tone="secondary">
                          {option.price}
                        </Text>
                      </Stack>
                      <Text variant="caption" tone="secondary">
                        {option.body}
                      </Text>
                    </Stack>
                    <Icon
                      name={isOn ? 'check_circle' : 'radio_button_unchecked'}
                      size={20}
                      filled={isOn}
                      className={cn(select.check, isOn && select.checked)}
                    />
                  </button>
                )
              })}
            </div>
          </Card>

          {isAgent && (
            <Card tone="light">
              <Text variant="caption" weight="semibold">
                What is this agent allowed to do?
              </Text>

              <Stack gap={5} className="pb-mt-4">
                <Field
                  label="What is it for?"
                  placeholder="Weekly groceries and pharmacy"
                  value={task}
                  onChange={(event) => setTask(event.target.value)}
                />

                <Stack gap={3}>
                  <Text variant="caption" weight="semibold">
                    Most it may spend in one transaction
                  </Text>
                  <Stack direction="row" gap={2} wrap>
                    {CAPS.map((value) => (
                      <Button
                        key={value}
                        variant={value === cap ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() => setCap(value)}
                      >
                        ${value}
                      </Button>
                    ))}
                  </Stack>
                </Stack>

                <Stack gap={3}>
                  <Text variant="caption" weight="semibold">
                    Where it may pay
                  </Text>
                  <Stack direction="row" gap={2} wrap>
                    {MERCHANTS.map((merchant) => {
                      const on = merchants.includes(merchant)
                      return (
                        <Button
                          key={merchant}
                          variant={on ? 'secondary' : 'outline'}
                          size="sm"
                          iconStart={on ? 'check' : 'add'}
                          onClick={() =>
                            setMerchants((current) =>
                              on ? current.filter((m) => m !== merchant) : [...current, merchant],
                            )
                          }
                        >
                          {merchant}
                        </Button>
                      )
                    })}
                  </Stack>
                  {merchants.length === 0 && (
                    <Stack direction="row" align="center" gap={2}>
                      <Icon name="error" size={16} />
                      <Text variant="caption" tone="negative" weight="semibold">
                        Pick at least one. A card with no merchants cannot pay anyone.
                      </Text>
                    </Stack>
                  )}
                </Stack>

                <Stack direction="row" align="start" gap={3}>
                  <Icon name="shield" size={20} />
                  <Text variant="caption" tone="secondary">
                    Anything outside these limits is declined, and you can revoke the card from
                    Cards at any moment. The agent never sees your other balances.
                  </Text>
                </Stack>
              </Stack>
            </Card>
          )}

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              What the card includes
            </Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {includedFor(choice).map((item) => (
                <div key={item.title} className={checkout.reason}>
                  <Icon name={item.icon} size={20} />
                  <Stack gap={1}>
                    <Text variant="caption" weight="semibold">
                      {item.title}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {item.body}
                    </Text>
                  </Stack>
                </div>
              ))}
            </div>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                Due today
              </Text>
              {choice === 'both' && <Pill tone="accent">Saves $4</Pill>}
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              ${price.toFixed(2)}
            </Text>

            <div className={checkout.rule} />

            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Card
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark">
                {CARD_OPTIONS.find((option) => option.id === choice)?.title}
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Monthly fee
              </Text>
              <Text variant="caption" weight="semibold" tone="accent">
                Free
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Ready in
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark">
                {choice === 'physical' || choice === 'both' ? 'About a week' : 'Seconds'}
              </Text>
            </div>

            {isAgent && (
              <>
                <div className={checkout.line}>
                  <Text variant="caption" tone="onDarkMuted">
                    Per transaction
                  </Text>
                  <Text variant="caption" weight="semibold" tone="onDark" numeric>
                    ${cap}.00
                  </Text>
                </div>
                <div className={checkout.line}>
                  <Text variant="caption" tone="onDarkMuted">
                    Merchants
                  </Text>
                  <Text variant="caption" weight="semibold" tone="onDark" numeric>
                    {merchants.length}
                  </Text>
                </div>
              </>
            )}

            <Card.Footer>
              <Button
                size="lg"
                block
                iconEnd="arrow_forward"
                disabled={isAgent && merchants.length === 0}
                onClick={() => navigate('/open/card/done')}
              >
                {isAgent ? 'Issue agent card' : `Pay $${price.toFixed(2)} and open card`}
              </Button>
            </Card.Footer>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              What you pay after this
            </Text>
            <Stack gap={0} className="pb-mt-2">
              {AFTER.map((row) => (
                <div key={row.label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {row.label}
                  </Text>
                  <Text variant="caption" weight="semibold" numeric>
                    {row.value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </Stack>
      </div>
    </>
  )
}
